import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Search as SearchIcon, Feather } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { T, SECTIONS_ORDER } from "../i18n/strings";

export default function Header() {
  const { lang, toggle } = useLanguage();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [now, setNow] = useState(new Date());
  const navigate = useNavigate();

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  const dateStr = now.toLocaleDateString(lang === "pt" ? "pt-BR" : "en-US", {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  const onSearch = (e) => {
    e.preventDefault();
    const q = e.target.q.value.trim();
    if (q) { navigate(`/search?q=${encodeURIComponent(q)}`); setOpen(false); }
  };

  return (
    <header className="w-full bg-[#FAF8F5]/95 backdrop-blur-md border-b border-[#C5A059]/30 sticky top-0 z-50" data-testid="site-header">
      {/* Top bar */}
      <div className="border-b border-[#081422]/10 bg-[#081422] text-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between text-xs font-mono uppercase tracking-widest">
          <span className="hidden sm:inline capitalize" data-testid="header-date">{dateStr}</span>
          <span className="hidden md:inline text-[#C5A059]">{T.edition[lang]}</span>
          <div className="flex items-center gap-3 ml-auto">
            <button
              onClick={toggle}
              className="hover:text-[#C5A059] transition-colors flex items-center gap-1.5"
              data-testid="language-switcher"
              aria-label="Switch language"
            >
              <span className={lang === "pt" ? "text-[#C5A059]" : ""}>PT</span>
              <span className="opacity-50">|</span>
              <span className={lang === "en" ? "text-[#C5A059]" : ""}>EN</span>
            </button>
            {user ? (
              <>
                <Link to="/admin" className="hover:text-[#C5A059] transition-colors" data-testid="header-admin-link">
                  {T.labels.admin[lang]}
                </Link>
                <button onClick={logout} className="hover:text-[#C5A059] transition-colors" data-testid="header-logout">
                  {T.labels.logout[lang]}
                </button>
              </>
            ) : (
              <Link to="/login" className="hover:text-[#C5A059] transition-colors" data-testid="header-login-link">
                {T.labels.login[lang]}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Brand */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center">
        <Link to="/" className="inline-block group" data-testid="brand-link">
          <div className="flex items-center justify-center gap-3 mb-1">
            <div className="hidden sm:block w-10 h-10 border-2 border-[#C5A059] pw-mullion" aria-hidden />
            <h1 className="font-serif-display text-3xl sm:text-5xl lg:text-6xl font-bold text-[#081422] tracking-tight leading-none">
              POLITICAL <span className="text-[#C5A059]">WINDOW</span>
            </h1>
            <div className="hidden sm:block w-10 h-10 border-2 border-[#C5A059] pw-mullion" aria-hidden />
          </div>
          <p className="font-mono-meta text-[0.7rem] sm:text-xs uppercase tracking-[0.28em] text-[#64748B] mt-2">
            {T.siteTagline[lang]}
          </p>
        </Link>
      </div>

      {/* Nav */}
      <nav className="border-t border-b border-[#081422]/10 bg-[#FAF8F5]" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="hidden lg:flex items-center gap-1 flex-wrap py-3">
              <NavItem to="/" label={T.nav.home[lang]} testId="nav-home" end />
              {SECTIONS_ORDER.map((s) => (
                <NavItem key={s} to={`/section/${s}`} label={T.nav[s][lang]} testId={`nav-${s}`} />
              ))}
            </div>
            <form onSubmit={onSearch} className="hidden lg:flex items-center gap-2 py-3">
              <SearchIcon size={16} className="text-[#64748B]" />
              <input
                name="q"
                placeholder={T.labels.search[lang]}
                className="bg-transparent border-b border-[#081422]/20 focus:border-[#C5A059] outline-none text-sm py-1 w-52 font-sans-ui"
                data-testid="header-search-input"
              />
            </form>
            <button
              className="lg:hidden p-3 ml-auto"
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
          {open && (
            <div className="lg:hidden pb-4 flex flex-col gap-1" data-testid="mobile-menu">
              <NavItem to="/" label={T.nav.home[lang]} testId="mnav-home" end mobile onClick={() => setOpen(false)} />
              {SECTIONS_ORDER.map((s) => (
                <NavItem key={s} to={`/section/${s}`} label={T.nav[s][lang]} testId={`mnav-${s}`} mobile onClick={() => setOpen(false)} />
              ))}
              <form onSubmit={onSearch} className="flex items-center gap-2 pt-3 border-t border-[#081422]/10 mt-2">
                <SearchIcon size={16} />
                <input name="q" placeholder={T.labels.search[lang]} className="pw-input" data-testid="mobile-search-input" />
              </form>
            </div>
          )}
        </div>
      </nav>
    </header>
  );
}

function NavItem({ to, label, testId, end, mobile, onClick }) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onClick}
      data-testid={testId}
      className={({ isActive }) =>
        `${mobile ? "block py-2 px-2" : "px-3 py-2"} font-sans-ui text-sm uppercase tracking-wider transition-colors ${
          isActive ? "text-[#C5A059] font-semibold" : "text-[#081422] hover:text-[#C5A059]"
        }`
      }
    >
      {label}
    </NavLink>
  );
}
